/**
 * Sistema de caché global para las llamadas a la API
 * Evita llamadas duplicadas y mejora el rendimiento
 */

// Cache global para almacenar respuestas
const cache = new Map();

// Peticiones en curso para evitar duplicados
const pendingRequests = new Map();

/**
 * Realiza una petición HTTP con caché
 * Si la petición ya está en caché, la devuelve
 * Si la petición está en curso, espera a que termine
 * Si no existe, la realiza y cachea el resultado
 * 
 * @param {string} url - URL del endpoint
 * @param {object} options - Opciones de fetch
 * @returns {Promise} - Promesa con los datos
 */
export const fetchWithCache = async (url, options = {}) => {
  const cacheKey = url;

  // 1. Si está en caché, devolver inmediatamente
  if (cache.has(cacheKey)) {
    /* console.log(`✅ Cache hit: ${url}`); */
    return Promise.resolve(cache.get(cacheKey));
  }

  // 2. Si la petición ya está en curso, esperar a que termine
  if (pendingRequests.has(cacheKey)) {
    /* console.log(`⏳ Waiting for pending request: ${url}`); */
    return pendingRequests.get(cacheKey);
  }

  // 3. Crear nueva petición
  /* console.log(`🔄 Fetching: ${url}`); */
  const fetchPromise = fetch(url, options)
    .then(async (response) => {
      if (response.status === 404) {
        const result = { status: 404, data: null };
        cache.set(cacheKey, result);
        return result;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP status: ${response.status}`);
      }

      const data = await response.json();
      const result = { status: 200, data };
      
      // Guardar en caché
      cache.set(cacheKey, result);
      return result;
    })
    .catch((error) => {
      // No cachear errores, permitir reintentos
      throw error;
    })
    .finally(() => {
      // Limpiar de peticiones pendientes
      pendingRequests.delete(cacheKey);
    });

  // Guardar como petición pendiente
  pendingRequests.set(cacheKey, fetchPromise);

  return fetchPromise;
};

/**
 * Limpia el caché completo o una entrada específica
 * @param {string} key - Clave específica a limpiar (opcional)
 */
export const clearCache = (key = null) => {
  if (key) {
    cache.delete(key);
    /* console.log(`🗑️ Cache cleared for: ${key}`); */
  } else {
    cache.clear();
    /* console.log('🗑️ Cache cleared completely'); */
  }
};

/**
 * Invalida el caché de un endpoint específico
 * @param {string} endpoint - Endpoint a invalidar (ej: 'hero', 'projects')
 */
export const invalidateEndpoint = (endpoint) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const url = `${backendUrl}/${endpoint}/`;
  clearCache(url);
};

/**
 * Hook helper para obtener el estado del caché
 */
export const getCacheStats = () => {
  return {
    size: cache.size,
    keys: Array.from(cache.keys()),
    pendingRequests: Array.from(pendingRequests.keys())
  };
};
